import type { MouseEvent } from 'react';

import { useAppDispatch } from '../store/hooks';
import { triggerGtmEvent } from '../misc/functions';
import { rgb2hex } from '../misc/color';
import { gtmEvents } from '../misc/gtm.constants';
import { NO_DATA } from '../misc/misc.constants';
import { openDrawer } from '../features/drawer/drawer.slice';
import { filtersUpdated } from '../features/table/table.slice';
import { COLUMNS } from '../misc/columns.constants';
import { FILTERS } from '../features/drawer/drawer.constants';
import type { WebsiteColumn } from '../types';

// Returns a table-body click handler implementing two cell shortcuts:
//   Alt+click  -> open the filters drawer and filter by the cell's value
//   Meta+click -> copy the cell's text (or theme color) to the clipboard
export function useTableCellActions(columns: WebsiteColumn[]) {
  const dispatch = useAppDispatch();

  function searchCell(event: MouseEvent<HTMLElement>) {
    if (!event.altKey) return;

    const cell = (event.target as HTMLElement).closest<HTMLElement>('td,th');
    if (!cell) return;

    const filterName = cell.dataset.qa;
    if (!filterName) return;

    for (const col of columns) {
      if (!col.renderFilter) continue;

      if (filterName !== col.name) continue;

      dispatch(openDrawer(FILTERS));

      setTimeout(() => {
        const filter = document.querySelector<HTMLInputElement>(
          `.filters input[data-qa='${filterName}']`,
        );
        if (!filter) return;

        let filterValue = '';
        if (![COLUMNS.pages, COLUMNS.forms].includes(filterName)) {
          filterValue = cell.innerText.trim();
        }

        dispatch(filtersUpdated({ [filterName]: filterValue }));
        triggerGtmEvent(gtmEvents.tableCellSearch, {
          filter_name: filterName,
          filter_value: filterValue,
        });

        filter.select();
      }, 300);
    }
  }

  async function copyCellText(event: MouseEvent<HTMLElement>) {
    if (!event.metaKey) return;

    const cell = (event.target as HTMLElement).closest<HTMLElement>('td,th');
    if (!cell) return;
    const qa = cell.dataset.qa ?? '';
    // skip list number, tags and ogImage columns
    if (
      ['#', COLUMNS.checkbox, COLUMNS.tags, COLUMNS.ogImage]
        .map((column) => column.toLowerCase())
        .includes(qa.toLowerCase())
    )
      return;

    const initColor = getComputedStyle(cell).color;
    const initBgColor = getComputedStyle(cell).backgroundColor;

    try {
      cell.style.color = getComputedStyle(document.body).getPropertyValue(
        '--body-color',
      );
      cell.style.backgroundColor = getComputedStyle(
        document.body,
      ).getPropertyValue('--body-bg');

      let contentToCopy: string;
      if (qa.toLowerCase().includes('theme')) {
        contentToCopy =
          initBgColor === 'rgba(0, 0, 0, 0)' ? NO_DATA : rgb2hex(initBgColor);
      } else {
        contentToCopy = cell.innerText;
      }
      await navigator.clipboard.writeText(contentToCopy);
    } catch (e) {
      console.log(`Error due to copy cell text content`, e);
    } finally {
      setTimeout(() => {
        if (
          qa.toLowerCase().includes('theme') &&
          initBgColor !== 'rgba(0, 0, 0, 0)'
        ) {
          cell.style.color = initColor;
          cell.style.backgroundColor = initBgColor;
        } else {
          cell.style.color = '';
          cell.style.backgroundColor = '';
        }
      }, 300);
    }
  }

  return function onTableBodyClick(event: MouseEvent<HTMLElement>) {
    searchCell(event);
    void copyCellText(event);
  };
}
